import type { PracticeSummary } from "../practice/types.ts";
import { buildTakeawayRule } from "@js/coaching.js";
import type { HintDepth } from "./hints.ts";
import { hintCreditLabel, masteryFromHintDepth } from "./hints.ts";
import type { QualityBandId } from "./quality.ts";
import { QUALITY_BANDS } from "./quality.ts";

export type RecapStudentMove = {
  ply: number;
  san: string;
  fenBefore: string;
  qualityBand: QualityBandId;
  assisted: boolean;
  deepestHint: HintDepth;
  isMainline: boolean;
  recommendedSan?: string | null;
  /** The course's move in this position; null where the course had none. */
  courseSan?: string | null;
  message?: string;
};

export type SessionRecap = {
  practice?: PracticeSummary;
  headline: string;
  independentCount: number;
  assistedCount: number;
  independentLabel: string;
  oneRule: string;
  onePosition: {
    moveNumber: number;
    san: string;
    fenBefore: string;
    note: string;
  } | null;
  firstDeviation: { moveNumber: number; san: string } | null;
  /** One line per five-move report card shown this session. */
  cards: Array<{ title: string; word: string; score: number }>;
  /** Course ideas realized on the board by the end of the session. */
  themes: { done: number; total: number; achieved: string[]; missed: string[] } | null;
  /** Moves played past the fifteen-move course. */
  freePlayMoves: number;
  /** The student's moves this session (a trap or a branch can end it early). */
  movesPlayed: number;
  /** The plan for the position the session ended in, when it reached move 15. */
  middlegamePlan?: string | null;
  /** "Top 31% across 15 ranked moves · 2 gold, 1 silver", or null without an engine. */
  rankSummary: string | null;
  /** A Repeat positions session: its answers, in place of a line played. */
  review?: {
    total: number;
    right: number;
    helped: number;
    /** The positions answered wrongly: what was played and the course move. */
    missed: Array<{ played: string | null; expected: string }>;
  } | null;
};

/** The recap of a Repeat positions session, built from its answers. */
export function buildReviewRecap(
  answers: Array<{
    pass: boolean;
    played: string | null;
    assisted: boolean;
    expected: string;
    repaired?: boolean;
  }>
): SessionRecap {
  const right = answers.filter((a) => a.pass).length;
  const helped = answers.filter((a) => a.pass && a.assisted).length;
  const missed = answers.filter((a) => !a.pass);
  const total = answers.length;
  const repairs = answers.filter((answer) => answer.repaired).length;
  return {
    headline: repairs
      ? `${right - repairs} found, ${repairs} repaired${missed.length ? `, ${missed.length} to revisit` : ""}.`
      : total === 0
        ? "No positions were due."
        : right === total
          ? helped
            ? "Every position right, some with help."
            : "Every position right."
          : `${right} of ${total} positions right.`,
    independentCount: right - helped,
    assistedCount: helped,
    independentLabel: `${right - helped} found alone · ${Math.max(0, helped - repairs)} with support · ${repairs} repaired · ${missed.length} to revisit`,
    oneRule:
      missed.length || repairs
        ? "The positions you missed come back tomorrow. Before each move there, say what the course move does."
        : helped
          ? "A useful practice with support. Try these decisions again without the cue on another visit."
          : "Positions you retrieve independently come back at longer intervals. Keep the daily habit short.",
    onePosition: null,
    firstDeviation: null,
    cards: [],
    themes: null,
    freePlayMoves: 0,
    movesPlayed: answers.length,
    middlegamePlan: null,
    rankSummary: null,
    review: {
      total,
      right,
      helped,
      missed: missed.map(({ played, expected }) => ({ played, expected })),
    },
  };
}

function weakestMove(moves: RecapStudentMove[]): RecapStudentMove | null {
  const rank: Record<QualityBandId, number> = {
    "tactical-error": 0,
    questionable: 1,
    playable: 2,
    "strong-alternative": 3,
    "best-practical": 4,
    // Unchecked is not weak: a move the engine never scored is not offered
    // to repeat ahead of one it did.
    "not-analyzed": 5,
  };
  // Only course positions can be repeated: past the end of a line or in free
  // play there is no course move to practice.
  const coursePositions = moves.filter((m) => m.courseSan !== null);
  // A strong move away from the course (an approved alternative) is not an
  // error to repeat.
  const strong = (m: RecapStudentMove) =>
    m.qualityBand === "best-practical" || m.qualityBand === "strong-alternative";
  const off = coursePositions.filter(
    (m) =>
      (!m.isMainline && !strong(m)) ||
      m.qualityBand === "tactical-error" ||
      m.qualityBand === "questionable"
  );
  // With nothing off the course, the one position worth repeating is one
  // that needed help; a clean session has none.
  const pool =
    off.length > 0 ? off : coursePositions.filter((m) => m.assisted || m.deepestHint > 0);
  if (pool.length === 0) return null;
  return [...pool].sort((a, b) => rank[a.qualityBand] - rank[b.qualityBand] || a.ply - b.ply)[0];
}

export function buildSessionRecap(args: {
  studentMoves: RecapStudentMove[];
  opening: { id?: string; name?: string } | null;
  positiveCounts: Record<string, number>;
  negativeCounts: Record<string, number>;
  firstDeviationPly: number | null;
  reportCards?: Array<{ title: string; word: string; score: number }>;
  themes?: { done: number; total: number; achieved: string[]; missed: string[] } | null;
  freePlayMoves?: number;
  movesPlayed?: number;
  rankSummary?: string | null;
}): SessionRecap {
  const independentCount = args.studentMoves.filter(
    (m) => masteryFromHintDepth(m.deepestHint) === "independent" && !m.assisted
  ).length;
  const assistedCount = args.studentMoves.length - independentCount;
  const independentRatio =
    args.studentMoves.length > 0 ? independentCount / args.studentMoves.length : 0;
  const hadTactic = args.studentMoves.some((m) => m.qualityBand === "tactical-error");

  const soundRatio =
    args.studentMoves.length > 0
      ? args.studentMoves.filter(
          (m) => m.qualityBand === "best-practical" || m.qualityBand === "strong-alternative"
        ).length / args.studentMoves.length
      : 0;
  // The headline weighs both how much was found alone and how good the moves
  // were: a hung queen is never "nicely played", and an all-best session with
  // hints is not a "tough one".
  let headline = "Tough one. Every session teaches.";
  if (args.studentMoves.length > 0 && independentRatio === 1 && !hadTactic) {
    headline = "Clean session.";
  } else if (hadTactic) {
    headline =
      soundRatio >= 0.7
        ? "Good work, with one costly slip to review."
        : "Tough one. Every session teaches.";
  } else if (independentRatio >= 0.7) headline = "Nicely played.";
  else if (soundRatio >= 0.7) headline = "Good moves. Next time, try more of them without hints.";
  else if (independentRatio >= 0.4) headline = "Good work — room to sharpen.";

  const weak = weakestMove(args.studentMoves);
  const onePosition = weak
    ? {
        moveNumber: Math.ceil(weak.ply / 2),
        san: weak.san,
        fenBefore: weak.fenBefore,
        note: `${QUALITY_BANDS[weak.qualityBand].label} on ${weak.san} (${hintCreditLabel(weak.deepestHint)}).`,
      }
    : null;

  const drift = args.studentMoves.find((m) => m.ply === args.firstDeviationPly) || null;

  return {
    headline,
    independentCount,
    assistedCount,
    independentLabel: `${independentCount} independent · ${assistedCount} assisted`,
    oneRule: buildTakeawayRule(
      args.studentMoves.map((m) => ({
        critique: { isMainline: m.isMainline, classification: m.isMainline ? "book" : "playable" },
      })),
      args.opening,
      args.positiveCounts,
      args.negativeCounts
    ),
    onePosition,
    firstDeviation: drift
      ? { moveNumber: Math.ceil(drift.ply / 2), san: drift.san }
      : args.firstDeviationPly
        ? { moveNumber: Math.ceil(args.firstDeviationPly / 2), san: "—" }
        : null,
    cards: args.reportCards || [],
    themes: args.themes || null,
    freePlayMoves: args.freePlayMoves || 0,
    movesPlayed: args.movesPlayed ?? args.studentMoves.length,
    rankSummary: args.rankSummary || null,
  };
}
